import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const SearchBar = ({ onSearch, searchResults, isSearching }) => {
  const [query, setQuery] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef?.current && !containerRef?.current?.contains(event?.target)) {
        setShowResults(false);
        setIsExpanded(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const value = e?.target?.value;
    setQuery(value);
    onSearch(value);
    setShowResults(value?.length > 0);
  };

  const handleFocus = () => {
    setIsExpanded(true);
    if (query?.length > 0) {
      setShowResults(true);
    }
  };

  const handleClear = () => {
    setQuery('');
    setShowResults(false);
    onSearch('');
    inputRef?.current?.focus();
  };

  const handleResultClick = (result) => {
    setQuery(result?.title);
    setShowResults(false);
    setIsExpanded(false);
  };

  const highlightText = (text, highlight) => {
    if (!highlight) return text;
    const parts = text?.split(new RegExp(`(${highlight})`, 'gi'));
    return parts?.map((part, index) => 
      part?.toLowerCase() === highlight?.toLowerCase() ? 
        <mark key={index} className="bg-accent/20 text-accent">{part}</mark> : part
    );
  };

  return (
    <div ref={containerRef} className="relative w-full max-w-2xl mx-auto">
      {/* Search Input */}
      <motion.div
        className={`relative bg-card border-2 rounded-2xl transition-all duration-300 shadow-3d ${
          isExpanded 
            ? 'border-accent/50 shadow-accent' 
            : 'border-border hover:border-accent/30'
        }`}
        animate={{
          scale: isExpanded ? 1.02 : 1,
        }}
        transition={{ duration: 0.2 }}
      >
        <div className="flex items-center px-6 py-4">
          <Icon 
            name="Search" 
            size={20} 
            className={`transition-colors duration-300 ${
              isExpanded ? 'text-accent' : 'text-muted-foreground'
            }`}
          />
          
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={handleInputChange}
            onFocus={handleFocus}
            placeholder="Search articles, tutorials, insights..."
            className="flex-1 ml-4 bg-transparent text-foreground placeholder-muted-foreground outline-none text-lg"
          />

          <AnimatePresence>
            {query && (
              <motion.button
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                onClick={handleClear}
                className="ml-2 p-1 text-muted-foreground hover:text-foreground transition-colors duration-200"
              >
                <Icon name="X" size={16} />
              </motion.button>
            )}
          </AnimatePresence>

          {isSearching && (
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              className="ml-2"
            >
              <Icon name="Loader2" size={16} className="text-accent" />
            </motion.div>
          )}
        </div>

        {/* Search Suggestions */}
        <AnimatePresence>
          {showResults && searchResults && searchResults?.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="absolute top-full left-0 right-0 mt-2 bg-card border border-border rounded-2xl shadow-3d overflow-hidden z-50"
            >
              <div className="p-4 border-b border-border">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-foreground">
                    Search Results
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {searchResults?.length} found
                  </span>
                </div>
              </div>

              <div className="max-h-96 overflow-y-auto">
                {searchResults?.slice(0, 5)?.map((result, index) => (
                  <motion.button
                    key={result?.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    onClick={() => handleResultClick(result)}
                    className="w-full p-4 text-left hover:bg-muted/30 transition-colors duration-200 border-b border-border last:border-b-0"
                  >
                    <div className="flex items-start space-x-3">
                      <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon name={result?.category?.icon} size={16} className="text-accent" />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-foreground mb-1 line-clamp-1">
                          {highlightText(result?.title, query)}
                        </h4>
                        <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                          {highlightText(result?.excerpt, query)}
                        </p>
                        <div className="flex items-center space-x-3 text-xs text-muted-foreground">
                          <span>{result?.category?.name}</span>
                          <span>•</span>
                          <span>{result?.readTime} min read</span>
                          <span>•</span>
                          <span>{result?.author?.name}</span>
                        </div>
                      </div>
                    </div>
                  </motion.button>
                ))}
              </div>

              {searchResults?.length > 5 && (
                <div className="p-4 border-t border-border text-center">
                  <button className="text-accent hover:text-accent/80 text-sm font-medium transition-colors duration-200">
                    View all {searchResults?.length} results
                  </button>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* No Results */}
        <AnimatePresence>
          {showResults && searchResults && searchResults?.length === 0 && query?.length > 2 && !isSearching && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="absolute top-full left-0 right-0 mt-2 bg-card border border-border rounded-2xl shadow-3d p-6 text-center z-50"
            >
              <Icon name="Search" size={32} className="text-muted-foreground mx-auto mb-3" />
              <h4 className="font-medium text-foreground mb-2">No articles found</h4>
              <p className="text-sm text-muted-foreground">
                Try searching with different keywords or browse our categories
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
      {/* Quick Search Tags */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="flex flex-wrap gap-2 mt-4 justify-center"
      >
        {['React', 'JavaScript', 'AI/ML', 'Web3', 'DevOps']?.map((tag, index) => (
          <motion.button
            key={tag}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3 + index * 0.1 }}
            onClick={() => {
              setQuery(tag);
              onSearch(tag);
              setShowResults(true);
            }}
            className="px-3 py-1.5 bg-muted/30 hover:bg-accent/10 text-muted-foreground hover:text-accent text-sm rounded-full transition-all duration-200 hover:scale-105"
          >
            {tag}
          </motion.button>
        ))}
      </motion.div>
    </div>
  );
};

export default SearchBar;